import React, { useState } from 'react';
import { Commission } from '../types/commission';
import { ChevronRight, Gem } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { EmployeeDetailPanel } from './EmployeeDetailPanel';
interface AdminViewProps {
  commissions: Commission[];
  onOpenFlaggedReview: (commission: Commission) => void;
  onViewUserCommission?: (userId: string) => void;
}
function getTierColor(units: number): {
  bg: string;
  text: string;
  border: string;
} {
  if (units >= 9) {
    return {
      bg: 'bg-amber-500/10',
      text: 'text-amber-400',
      border: 'border-amber-500/20'
    };
  } else if (units >= 4) {
    return {
      bg: 'bg-blue-500/10',
      text: 'text-blue-400',
      border: 'border-blue-500/20'
    };
  } else {
    return {
      bg: 'bg-slate-500/10',
      text: 'text-slate-300',
      border: 'border-slate-500/20'
    };
  }
}
function getBadgeColor(badge: string): {
  bg: string;
  text: string;
  border: string;
} {
  const colors = {
    Diamond: {
      bg: 'bg-blue-500/10',
      text: 'text-blue-400',
      border: 'border-blue-500/20'
    },
    Platinum: {
      bg: 'bg-purple-500/10',
      text: 'text-purple-400',
      border: 'border-purple-500/20'
    },
    Gold: {
      bg: 'bg-yellow-500/10',
      text: 'text-yellow-400',
      border: 'border-yellow-500/20'
    }
  };
  return colors[badge as keyof typeof colors] || colors.Gold;
}
function getTierName(units: number): string {
  if (units >= 9) return '9+ Units';
  if (units >= 4) return '4-8 Units';
  return '1-3 Units';
}
export function AdminView({
  commissions,
  onOpenFlaggedReview,
  onViewUserCommission
}: AdminViewProps) {
  const [selectedEmployee, setSelectedEmployee] = useState<Commission | null>(null);
  return <>
      <div className="bg-navy-800/70 backdrop-blur-sm rounded-2xl border border-white/5 shadow-lg overflow-hidden" data-id="element-15">
        {/* Table Header */}
        <div className="relative border-b border-white/5 px-8 py-6" data-id="element-16">
          <div className="relative" data-id="element-17">
            <h2 className="text-xl font-bold text-white mb-1" data-id="element-18">
              Team Commissions
            </h2>
            <p className="text-sm text-slate-400 font-medium" data-id="element-19">
              {commissions.length} team member
              {commissions.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto" data-id="element-20">
          <table className="w-full" data-id="element-21">
            <thead data-id="element-22">
              <tr className="border-b border-white/5" data-id="element-23">
                <th className="px-8 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-24">
                  Employee
                </th>
                <th className="px-8 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-25">
                  Title
                </th>
                <th className="px-8 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-26">
                  Current Tier
                </th>
                <th className="px-8 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-27">
                  Current Badge
                </th>
                <th className="px-8 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-28">
                  Total Loan Volume
                </th>
                <th className="px-8 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-29">
                  Total Commission
                </th>
                <th className="px-8 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-30">
                  Status
                </th>
                <th className="px-8 py-4" data-id="element-31"></th>
              </tr>
            </thead>
            <tbody data-id="element-32">
              {commissions.map((commission, index) => {
              const tierName = getTierName(commission.units);
              const tierColors = getTierColor(commission.units);
              const totalLoanVolume = commission.loans.reduce((acc, loan) => acc + loan.amount, 0);
              const badge = commission.profile?.badge || 'Gold';
              const badgeColors = getBadgeColor(badge);
              const flaggedCount = commission.loans.filter(l => ['flagged', 'under_review'].includes(l.approvalStatus)).length;
              const approvedCount = commission.loans.filter(l => l.approvalStatus === 'approved').length;
              const totalLoans = commission.loans.length;
              const status = commission.review?.status || 'pending';
              return <motion.tr key={commission.userId} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.05 * index
              }} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors group cursor-pointer" onClick={() => setSelectedEmployee(commission)} data-id="element-33">
                    <td className="px-8 py-6 whitespace-nowrap" data-id="element-34">
                      <div className="flex items-center gap-3" data-id="element-35">
                        <div className="w-10 h-10 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-sm" data-id="element-36">
                          {commission.userName.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="text-sm font-semibold text-slate-200 group-hover:text-accent-400 transition-colors" data-id="element-37">
                          {commission.userName}
                        </span>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap" data-id="element-38">
                      <span className="text-sm text-slate-400 font-medium" data-id="element-39">
                        {commission.userProfile}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-center" data-id="element-40">
                      <span className={`inline-flex items-center px-3 py-1 rounded-lg border ${tierColors.bg} ${tierColors.text} ${tierColors.border} text-xs font-bold`} data-id="element-41">
                        {tierName}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-center" data-id="element-42">
                      <span className={`inline-flex items-center px-3 py-1 rounded-lg border ${badgeColors.bg} ${badgeColors.text} ${badgeColors.border} text-xs font-bold`} data-id="element-43">
                        {badge}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-right" data-id="element-44">
                      <span className="text-sm font-semibold text-slate-200 tabular-nums" data-id="element-45">
                        $
                        {totalLoanVolume.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-right" data-id="element-46">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-sm font-bold text-emerald-400 tabular-nums border border-emerald-500/20" data-id="element-47">
                        $
                        {commission.total.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-center" data-id="element-48">
                      {status === 'approved' && <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold border border-emerald-500/20" data-id="element-49">
                          Approved
                        </span>}
                      {status === 'flagged' && <button onClick={e => {
                    e.stopPropagation();
                    onOpenFlaggedReview(commission);
                  }} className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold hover:bg-amber-500/20 transition-colors border border-amber-500/20" data-id="element-50">
                          {flaggedCount} Flagged
                        </button>}
                      {status === 'under_review' && <button onClick={e => {
                    e.stopPropagation();
                    onOpenFlaggedReview(commission);
                  }} className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-semibold hover:bg-blue-500/20 transition-colors border border-blue-500/20" data-id="element-51">
                          Under Review
                        </button>}
                      {status === 'resolved_pending_approval' && <button onClick={e => {
                    e.stopPropagation();
                    onOpenFlaggedReview(commission);
                  }} className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold hover:bg-emerald-500/20 transition-colors border border-emerald-500/20" data-id="element-52">
                          Resolved
                        </button>}
                      {status === 'pending' && <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-500/10 text-slate-400 text-xs font-semibold border border-slate-500/20" data-id="element-53">
                          {approvedCount}/{totalLoans} Approved
                        </span>}
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-right" data-id="element-54">
                      <div className="flex items-center justify-end gap-2" data-id="element-55">
                        {onViewUserCommission && <button onClick={e => {
                      e.stopPropagation();
                      onViewUserCommission(commission.userId);
                    }} className="px-3 py-1.5 rounded-lg bg-accent-500/10 text-accent-400 text-xs font-semibold border border-accent-500/20 hover:bg-accent-500/20 transition-all opacity-0 group-hover:opacity-100" data-id="element-56">
                            View Commission
                          </button>}
                        <ChevronRight size={20} className="text-slate-600 group-hover:text-accent-400 transition-colors" data-id="element-57" />
                      </div>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Employee Detail Panel */}
      <AnimatePresence data-id="element-58">
        {selectedEmployee && <EmployeeDetailPanel commission={selectedEmployee} onClose={() => setSelectedEmployee(null)} data-id="element-59" />}
      </AnimatePresence>
    </>;
}